import { useState, useEffect } from "react";
import axios from "../../utils/api/axios";
import CoinIcon from "../../assets/icons/CoinIcon";
import {
  CTBProgram,
  ProgramType
} from "../../utils/api/endpoints/programs/parsePrograms";
import toast from "react-hot-toast";
import moment from "moment";
import useUserDetails from "../../utils/hooks/user/useUserDetails";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { useUpdateProgramDatesMutation } from "../../utils/api/endpoints/programsApi";
// Removed collapsible to match flat section UI while keeping content
import RichTextRenderer from "../common/RichTextRenderer";
import { ProgramCredentialViewer } from "../vault";
import {
  HiOutlineExclamationCircle,
  HiOutlineShieldCheck,
  HiOutlineDocumentText
} from "react-icons/hi";
import {
  FiTarget,
  FiActivity,
  FiCalendar,
  FiClock,
  FiEdit2,
  FiCheck,
  FiX
} from "react-icons/fi";

interface ProgramRun {
  run_id: number;
  run_title: string;
  status: string;
  release_notes?: string;
  business_priorities?: string;
}

const FullProgramDetails = ({ program }: { program: CTBProgram }) => {
  // Helper to check if Program Details (description + technical context) exists
  const hasProgramDetailsData = !!(
    (program.description && program.description.trim() !== "" && program.description.trim() !== "<p></p>") ||
    (program.targets && program.targets.length > 0) ||
    (program.credentials && program.credentials.trim() !== "" && program.credentials.trim() !== "<p></p>") ||
    (program.vpn && program.vpn.trim() !== "" && program.vpn.trim() !== "<p></p>")
  );

  const [activeSection, setActiveSection] = useState(hasProgramDetailsData ? "Program Details" : "Scheduling Strategy");
  const { role } = useUserCredentials();
  const [updateDates] = useUpdateProgramDatesMutation();

  const [isEditingDates, setIsEditingDates] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(program.expected_start_date ? moment(program.expected_start_date).format('YYYY-MM-DD') : '');
  const [tempEndDate, setTempEndDate] = useState(program.expected_end_date ? moment(program.expected_end_date).format('YYYY-MM-DD') : '');

  const isAdmin = role === UserRole.ADMIN || role === UserRole.SUB_ADMIN;
  const isV2 = (program as any).architecture_version >= 2;
  const canEditDates = isAdmin && isV2;

  const [runs, setRuns] = useState<ProgramRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  useEffect(() => {
    const fetchRuns = async () => {
      if (!program.id) return;
      setLoadingRuns(true);
      try {
        const response = await axios.get(`/v2/programs/${program.id}/runs`);
        setRuns(response.data.data || []);
      } catch (error) {
        console.error("Error fetching program runs:", error);
      } finally {
        setLoadingRuns(false);
      }
    };

    if (isV2) {
      fetchRuns();
    }
  }, [program.id, isV2]);

  const handleSaveDates = async () => {
    if (!program.id) return;
    const loader = toast.loading("Updating dates...");
    try {
      await updateDates({
        id: program.id,
        expected_start_date: tempStartDate,
        expected_end_date: tempEndDate
      }).unwrap();
      toast.success("Dates updated successfully");
      setIsEditingDates(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update dates");
    } finally {
      toast.remove(loader);
    }
  };

  const handleCancelDates = () => {
    setTempStartDate(program.expected_start_date ? moment(program.expected_start_date).format('YYYY-MM-DD') : '');
    setTempEndDate(program.expected_end_date ? moment(program.expected_end_date).format('YYYY-MM-DD') : '');
    setIsEditingDates(false);
  };

  const getWordCount = (content?: string) => {
    return content ? content.trim().split(/\s+/).length : 0;
  };

  // Helper to check if scheduling data exists
  const hasSchedulingData = !!(
    program.scheduling_type ||
    program.recurrence ||
    program.expected_start_date ||
    program.expected_end_date
  );

  // Helper to check if context data exists
  const hasContextData = !!(
    program.release_notes ||
    program.scope ||
    program.other
  );

  // Helper to check if RoE data exists
  const hasRoeData = !!(program.custom_roe && program.custom_roe.trim().length > 0);

  const sections = [
    { label: "Program Details", content: program.description, alwaysShow: hasProgramDetailsData },
    { label: "Scheduling Strategy", content: "scheduling", alwaysShow: true, isCustom: true },
    { label: "Context & Test Focus", content: "context", alwaysShow: true, isCustom: true },
    { label: "Rules of Engagement", content: "roe", alwaysShow: true, isCustom: true },
    { label: "Out Of Scope", content: program.outOfScope },
    { label: "Reward Policy", content: program.rewardPolicy },
    { label: "Other", content: program.other }
  ].filter(
    (section) => section.alwaysShow || getWordCount(section.content) > 0
  );

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
  };

  const PayoutRangeCard = ({
    label,
    amount,
    className
  }: {
    label: string;
    amount: number;
    className?: string;
  }) => (
    <div
      className={`flex w-full flex-col items-center justify-center gap-3 py-4 ${className}`}
    >
      <p className="text-sm font-medium">${amount.toFixed(2)}</p>
      <p className="text-sm">{label}</p>
    </div>
  );

  return (
    <div className="bg-white p-3 pb-10 rounded-lg">
      <div className="flex flex-col gap-6 mt-4">
        {/* Tab Navigation */}
        <div className="bg-[#00081D] p-4 rounded-xl flex gap-1.5 overflow-x-auto">
          {sections.map(({ label }) => (
            <button
              key={label}
              className={`px-5 py-2 rounded-lg text-[13px] transition-all duration-200 whitespace-nowrap ${activeSection === label
                ? "bg-[#76D301] text-white shadow-lg shadow-green-500/20"
                : "font-medium text-slate-300 hover:text-white hover:bg-white/5"
                }`}
              onClick={() => handleSectionChange(label)}
              role="tab"
              aria-selected={activeSection === label}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Payout Range Section */}
        {program.payoutRange && program.type === ProgramType.BugBounty && (
          <article className="rounded-lg border-[1px] border-ctb-blue-50 p-5">
            <div className="mb-3 flex items-center gap-3">
              <CoinIcon className="stroke-ctb-blue-400" />
              <h2 className="text-sm text-black">Payout Range</h2>
            </div>
            <div className="flex w-full">
              <PayoutRangeCard
                label="Low"
                amount={program.payoutRange.low || 0}
                className="rounded-s-lg bg-ctb-green-200 text-white"
              />
              <PayoutRangeCard
                label="Medium"
                amount={program.payoutRange.medium || 0}
                className="bg-ctb-yellow-200 text-white"
              />
              <PayoutRangeCard
                label="High"
                amount={program.payoutRange.high || 0}
                className="bg-ctb-red-200 text-white"
              />
              <PayoutRangeCard
                label="Critical"
                amount={program.payoutRange.critical || 0}
                className="rounded-e-lg bg-ctb-red-500 text-white"
              />
            </div>
          </article>
        )}

        {/* Scheduling Strategy Section */}
        {activeSection === "Scheduling Strategy" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-1">
            {/* Engagement Type */}
            <div className="bg-[#F8FAFC] rounded-2xl p-6 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#E9F7EF] rounded-lg">
                    <FiTarget className="w-4 h-4 text-[#27AE60]" />
                  </div>
                  <span className="text-[12px] font-medium text-slate-400">Engagement Type</span>
                </div>
                {canEditDates && !isEditingDates && (
                  <button
                    onClick={() => setIsEditingDates(true)}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-emerald-600 transition-all flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider"
                  >
                    <FiEdit2 className="w-3.5 h-3.5" />
                    Edit Dates
                  </button>
                )}
                {isEditingDates && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveDates}
                      className="p-2 hover:bg-emerald-50 rounded-lg text-emerald-600 transition-all flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider"
                      title="Save Changes"
                    >
                      <FiCheck className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={handleCancelDates}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-500 transition-all flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider"
                      title="Cancel"
                    >
                      <FiX className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <p className="text-[18px] font-bold text-[#0F172A] capitalize">
                {program.scheduling_type?.replace('_', ' ') || 'One Off'}
              </p>
            </div>

            {hasSchedulingData ? (
              <>
                {/* Frequency */}
                {program.scheduling_type === 'continuous' && program.recurrence && (
                  <div className="bg-[#F8FAFC] rounded-2xl p-6 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#E9F7EF] rounded-lg">
                        <FiActivity className="w-4 h-4 text-[#27AE60]" />
                      </div>
                      <span className="text-[12px] font-medium text-slate-400">Frequency</span>
                    </div>
                    <p className="text-[18px] font-bold text-[#0F172A] capitalize">
                      {program.recurrence.replace('_', ' ')}
                    </p>
                  </div>
                )}

                {/* Start Date */}
                {(program.expected_start_date || isEditingDates) && (
                  <div className="bg-[#F8FAFC] rounded-2xl p-6 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#E9F7EF] rounded-lg">
                        <FiCalendar className="w-4 h-4 text-[#27AE60]" />
                      </div>
                      <span className="text-[12px] font-medium text-slate-400">
                        {program.scheduling_type === 'one_off' ? 'Start Date' : 'Program Start Date'}
                      </span>
                    </div>
                    {isEditingDates ? (
                      <input
                        type="date"
                        value={tempStartDate}
                        onChange={(e) => setTempStartDate(e.target.value)}
                        className="w-full h-10 rounded-xl border border-slate-200 px-4 text-[16px] font-bold text-[#0F172A] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all shadow-sm"
                      />
                    ) : (
                      <p className="text-[18px] font-bold text-[#0F172A]">
                        {new Date(program.expected_start_date!).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                )}

                {/* End Date */}
                {(program.expected_end_date || isEditingDates) && (
                  <div className="bg-[#F8FAFC] rounded-2xl p-6 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#E9F7EF] rounded-lg">
                        <FiClock className="w-4 h-4 text-[#27AE60]" />
                      </div>
                      <span className="text-[12px] font-medium text-slate-400">Program End Date</span>
                    </div>
                    {isEditingDates ? (
                      <input
                        type="date"
                        value={tempEndDate}
                        onChange={(e) => setTempEndDate(e.target.value)}
                        className="w-full h-10 rounded-xl border border-slate-200 px-4 text-[16px] font-bold text-[#0F172A] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all shadow-sm"
                      />
                    ) : (
                      <p className="text-[18px] font-bold text-[#0F172A]">
                        {new Date(program.expected_end_date!).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              // Default text if no scheduling data
              <div className="bg-[#F8FAFC] rounded-2xl p-6 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#E9F7EF] rounded-lg">
                    <FiCalendar className="w-4 h-4 text-[#27AE60]" />
                  </div>
                  <span className="text-[12px] font-medium text-slate-400">Schedule</span>
                </div>
                <p className="text-[16px] font-medium text-slate-500">
                  Standard immediate engagement upon activation.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Context & Test Focus Section */}
        {activeSection === "Context & Test Focus" && (
          <div className="space-y-6 px-1">
            {hasContextData ? (
              <>
                {program.release_notes && program.release_notes.trim().length > 0 && (
                  <div className="bg-[#F1F9E8] border border-[#DDEFD2] rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-5 h-5 flex items-center justify-center text-[#76D301]">
                        <HiOutlineExclamationCircle className="w-5 h-5" />
                      </div>
                      <h4 className="text-[15px] font-bold text-[#0F172A]">New Features / Release Notes</h4>
                    </div>
                    <div className="text-[15px] leading-relaxed text-slate-500">
                      <RichTextRenderer content={program.release_notes} />
                    </div>
                  </div>
                )}
                {program.scope && program.scope.trim().length > 0 && (
                  <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
                    <h4 className="text-[15px] font-bold text-[#0F172A] mb-4">Business Priorities</h4>
                    <div className="text-[14px] leading-relaxed text-slate-500">
                      <RichTextRenderer content={program.scope} />
                    </div>
                  </div>
                )}
                {program.other && program.other.trim().length > 0 && (
                  <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
                    <h4 className="text-[15px] font-bold text-[#0F172A] mb-4">Special Instructions</h4>
                    <div className="text-[14px] leading-relaxed text-slate-500">
                      <RichTextRenderer content={program.other} />
                    </div>
                  </div>
                )}

                {/* Per-Run Context for V2 Programs */}
                {isV2 && runs.filter(r => (r.status === 'active' || r.status === 'scheduled') && (r.release_notes || r.business_priorities)).map((run) => (
                  <div key={run.run_id} className="bg-white border-2 border-emerald-100 rounded-2xl overflow-hidden shadow-sm">
                    <div className="bg-emerald-50 px-6 py-3 border-b border-emerald-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                        <h4 className="text-[13px] font-black text-emerald-800 uppercase tracking-wider">Run Context: {run.run_title}</h4>
                      </div>
                      <span className="text-[10px] font-bold text-emerald-600 bg-white px-2 py-0.5 rounded-md border border-emerald-100 capitalize">
                        {run.status}
                      </span>
                    </div>
                    <div className="p-6 space-y-4">
                      {run.release_notes && (
                        <div>
                          <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-2">Release Notes</label>
                          <div className="text-[14px] leading-relaxed text-slate-600 italic bg-slate-50 p-4 rounded-xl border border-slate-100">
                            {run.release_notes}
                          </div>
                        </div>
                      )}
                      {run.business_priorities && (
                        <div>
                          <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-2">Run Priorities</label>
                          <div className="text-[14px] leading-relaxed text-slate-600 italic bg-slate-50 p-4 rounded-xl border border-slate-100">
                            {run.business_priorities}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-5 h-5 flex items-center justify-center text-slate-400">
                    <HiOutlineExclamationCircle className="w-5 h-5" />
                  </div>
                  <h4 className="text-[15px] font-bold text-[#0F172A]">Standard & Business Priorities</h4>
                </div>
                <div className="text-[14px] leading-relaxed text-slate-500">
                  <p>In the absence of specific testing instructions, our security team will proceed with a standard, comprehensive vulnerability assessment. This includes checking for OWASP Top 10 vulnerabilities, unauthorized access risks, business logic flaws, and data leakage.</p>
                  <br />
                  <p>Testers are advised to focus on critical workflows and sensitive data protection mechanisms standard for this asset type.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rules of Engagement Section */}
        {activeSection === "Rules of Engagement" && (
          <div className="space-y-6 px-1">
            <div className="bg-[#F8FAFC] border border-slate-100 rounded-2xl p-6 flex items-start gap-4">
              <div className="p-2 bg-[#E9F7EF] rounded-lg">
                <HiOutlineShieldCheck className="w-5 h-5 text-[#27AE60]" />
              </div>
              <div>
                <h4 className="text-[15px] font-bold text-[#0F172A] mb-1">Standard Rules</h4>
                <p className="text-[13px] text-slate-500">Standard security boundaries (No DoS, No Social Engineering, No destructive testing) apply by default.</p>
              </div>
            </div>
            {program.custom_roe && program.custom_roe.trim().length > 0 ? (
              <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <HiOutlineDocumentText className="w-5 h-5 text-slate-400" />
                  </div>
                  <h4 className="text-[15px] font-bold text-[#0F172A]">Custom Rules</h4>
                </div>
                <div className="text-[14px] leading-relaxed text-slate-500">
                  <RichTextRenderer content={program.custom_roe} />
                </div>
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm opacity-75">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <HiOutlineDocumentText className="w-5 h-5 text-slate-400" />
                  </div>
                  <h4 className="text-[15px] font-bold text-[#0F172A]">Custom Rules</h4>
                </div>
                <div className="text-[14px] leading-relaxed text-slate-500 italic">
                  No additional custom rules specified. Please adhere strictly to the Standard Rules of Engagement.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Standard Section Content (Out of Scope, Reward Policy, Other) */}
        {sections.map(({ label, content, isCustom }) =>
          activeSection === label && !isCustom && content ? (
            <div key={label} className="px-1">
              <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
                <h4 className="text-[16px] font-bold text-[#0F172A] mb-4">
                  {label === "Program Details" ? "Description" : label}
                </h4>
                <div className="text-[14px] leading-relaxed text-slate-500">
                  <RichTextRenderer content={content} />
                </div>
              </div>
            </div>
          ) : null
        )}

        {/* Program Details extras: Targets, Credentials, VPN - Only show if data exists */}
        {activeSection === "Program Details" && (
          <div className="px-4">
            {/* Targets - Only show if data exists */}
            {program.targets && program.targets.length > 0 && (
              <>
                <div className="border-t border-[#E5E5E5] my-4" />
                <h4 className="text-[16px] font-bold text-black mb-3">Targets</h4>
                <div className="mb-8 text-[15px] text-[#494949]">
                  <div className="flex flex-col gap-4">
                    {program.targets.map((t, idx) => (
                      <div key={idx} className="text-[#0B45DB]">{t.targetName}</div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="border-t border-[#E5E5E5] my-6" />
            <h4 className="text-[16px] font-bold text-black mb-3">Secure Credential Vault</h4>
            <div className="mb-8">
              {/*
              ProgramCredentialViewer uses ACCESS PASSWORD (not master password)
              - Business owners manage credentials via Credential Vault page (sidebar)
              - On program page, everyone uses access password to view shared credentials
            */}
              <ProgramCredentialViewer programId={program.id} />
            </div>

            {/* Legacy Credentials (for backward compatibility) */}
            {program.credentials && program.credentials.trim().length > 0 && (
              <>
                <div className="border-t border-[#E5E5E5] my-6" />
                <h4 className="text-[18px] font-normal text-black mb-3">Additional Credentials Info</h4>
                <div className="mb-8 text-[15px] text-[#494949]">
                  <RichTextRenderer content={program.credentials} />
                </div>
              </>
            )}

            {/* VPN - Only show if data exists */}
            {program.vpn && program.vpn.trim().length > 0 && (
              <>
                <div className="border-t border-[#E5E5E5] my-6" />
                <h4 className="text-[18px] font-normal text-black mb-3">VPN Details</h4>
                <div className="mb-2 text-[15px] text-[#494949]">
                  <RichTextRenderer content={program.vpn} />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FullProgramDetails;
